import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import { seasonService } from '../../domain/content/service/seasonService';
import { SeasonData, SeasonApiRequest } from 'type/seasonType';
import { FetchResult } from 'type/apiType';

// Query Keys
export const SEASON_QUERY_KEYS = {
  all: ['seasons'] as const,
  lists: () => [...SEASON_QUERY_KEYS.all, 'list'] as const,
  list: (params?: URLSearchParams) => [...SEASON_QUERY_KEYS.lists(), params?.toString()] as const,
  details: () => [...SEASON_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: number) => [...SEASON_QUERY_KEYS.details(), id] as const,
  byLanguage: (language: string) => [...SEASON_QUERY_KEYS.all, 'language', language] as const,
  byTranslationStatus: (languages: string[]) =>
    [...SEASON_QUERY_KEYS.all, 'translation', languages.join(',')] as const,
  bySeries: (seriesId: number) => [...SEASON_QUERY_KEYS.all, 'series', seriesId] as const,
};

/**
 * i18n 필드를 포함한 Season 목록 조회 훅
 */
export const useSeasonList = (
  queryParams?: URLSearchParams,
  options?: Omit<UseQueryOptions<FetchResult<SeasonData>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: SEASON_QUERY_KEYS.list(queryParams),
    queryFn: () => seasonService.fetchSeasonsWithI18n(queryParams),
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
    ...options,
  });
};

/**
 * i18n 필드를 포함한 단일 Season 조회 훅
 */
export const useSeason = (
  id: number,
  options?: Omit<UseQueryOptions<SeasonData>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: SEASON_QUERY_KEYS.detail(id),
    queryFn: () => seasonService.fetchSeasonWithI18n(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
    ...options,
  });
};

/**
 * 특정 언어의 Season 목록 조회 훅
 */
export const useSeasonsByLanguage = (
  language: string,
  queryParams?: URLSearchParams,
  options?: Omit<UseQueryOptions<FetchResult<SeasonData>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: SEASON_QUERY_KEYS.byLanguage(language),
    queryFn: () => seasonService.fetchSeasonsByLanguage(language, queryParams),
    enabled: !!language,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
    ...options,
  });
};

/**
 * 번역 완성도별 Season 조회 훅
 */
export const useSeasonsByTranslationStatus = (
  requiredLanguages: string[] = ['ko'],
  queryParams?: URLSearchParams,
  options?: Omit<UseQueryOptions<FetchResult<SeasonData>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: SEASON_QUERY_KEYS.byTranslationStatus(requiredLanguages),
    queryFn: () => seasonService.fetchSeasonsByTranslationStatus(requiredLanguages, queryParams),
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
    ...options,
  });
};

/**
 * 특정 Series에 속한 Season 목록 조회 훅 (i18n 지원)
 */
export const useSeasonsBySeries = (
  seriesId: number,
  queryParams?: URLSearchParams,
  options?: Omit<UseQueryOptions<FetchResult<SeasonData>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: SEASON_QUERY_KEYS.bySeries(seriesId),
    queryFn: () => seasonService.fetchSeasonsBySeriesWithI18n(seriesId, queryParams),
    enabled: !!seriesId,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
    ...options,
  });
};

/**
 * Season 생성 뮤테이션 훅
 */
export const useCreateSeason = (
  options?: UseMutationOptions<SeasonData, Error, SeasonApiRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (seasonData: SeasonApiRequest) => seasonService.createSeasonWithI18n(seasonData),
    onSuccess: data => {
      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.lists() });

      // 새로 생성된 Season을 캐시에 추가
      queryClient.setQueryData(SEASON_QUERY_KEYS.detail(data.id), data);

      // 해당 Series의 Season 목록 캐시 무효화
      if (data.series_id) {
        queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.bySeries(data.series_id) });
      }
    },
    ...options,
  });
};

/**
 * Season 업데이트 뮤테이션 훅
 */
export const useUpdateSeason = (
  options?: UseMutationOptions<SeasonData, Error, { id: number; data: Partial<SeasonApiRequest> }>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => seasonService.updateSeasonWithI18n(id, data),
    onSuccess: (data, variables) => {
      // 특정 Season 캐시 업데이트
      queryClient.setQueryData(SEASON_QUERY_KEYS.detail(variables.id), data);

      // 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.lists() });

      // 언어별 캐시 무효화
      queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.all });

      // 해당 Series의 Season 목록 캐시 무효화
      if (data.series_id) {
        queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.bySeries(data.series_id) });
      }
    },
    ...options,
  });
};

/**
 * Season 삭제 뮤테이션 훅
 */
export const useDeleteSeason = (options?: UseMutationOptions<void, Error, number>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => seasonService.delete(id),
    onSuccess: (_, id) => {
      // 삭제된 Season 캐시 제거
      queryClient.removeQueries({ queryKey: SEASON_QUERY_KEYS.detail(id) });

      // 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.lists() });

      // 언어별 캐시 무효화
      queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.all });
    },
    ...options,
  });
};

/**
 * Season 캐시 프리페치 훅
 */
export const usePrefetchSeason = () => {
  const queryClient = useQueryClient();

  const prefetchSeason = (id: number) => {
    queryClient.prefetchQuery({
      queryKey: SEASON_QUERY_KEYS.detail(id),
      queryFn: () => seasonService.fetchSeasonWithI18n(id),
      staleTime: 5 * 60 * 1000, // 5분
    });
  };

  const prefetchSeasonList = (queryParams?: URLSearchParams) => {
    queryClient.prefetchQuery({
      queryKey: SEASON_QUERY_KEYS.list(queryParams),
      queryFn: () => seasonService.fetchSeasonsWithI18n(queryParams),
      staleTime: 5 * 60 * 1000, // 5분
    });
  };

  const prefetchSeasonsBySeries = (seriesId: number, queryParams?: URLSearchParams) => {
    queryClient.prefetchQuery({
      queryKey: SEASON_QUERY_KEYS.bySeries(seriesId),
      queryFn: () => seasonService.fetchSeasonsBySeriesWithI18n(seriesId, queryParams),
      staleTime: 5 * 60 * 1000, // 5분
    });
  };

  return { prefetchSeason, prefetchSeasonList, prefetchSeasonsBySeries };
};

/**
 * Season 캐시 무효화 훅
 */
export const useInvalidateSeasonCache = () => {
  const queryClient = useQueryClient();

  const invalidateAllSeasons = () => {
    queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.all });
  };

  const invalidateSeasonLists = () => {
    queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.lists() });
  };

  const invalidateSeason = (id: number) => {
    queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.detail(id) });
  };

  const invalidateSeasonsByLanguage = (language: string) => {
    queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.byLanguage(language) });
  };

  const invalidateSeasonsBySeries = (seriesId: number) => {
    queryClient.invalidateQueries({ queryKey: SEASON_QUERY_KEYS.bySeries(seriesId) });
  };

  return {
    invalidateAllSeasons,
    invalidateSeasonLists,
    invalidateSeason,
    invalidateSeasonsByLanguage,
    invalidateSeasonsBySeries,
  };
};
